import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import BarSettings from "@/components/bar/BarSettings";
import BarRoom from "@/components/bar/BarRoom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const ym = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;

export default function Bar() {
  const { isSuperAdmin, isBarman } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("camere");
  const now = new Date();
  const [y, setY] = useState(now.getFullYear());
  const [m, setM] = useState(now.getMonth());
  const month = ym(y, m);
  const shift = (n) => { const t = new Date(y, m + n, 1); setY(t.getFullYear()); setM(t.getMonth()); };
  const load = async () => {
    try { setData((await api.get(`/bar/rooms?month=${month}`)).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (isSuperAdmin || isBarman) { load(); } }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!isSuperAdmin && !isBarman) return <Navigate to="/calendario" replace />;
  const tabs = [["camere", "Camere"], ...(isSuperAdmin ? [["listino", "Listino prezzi"]] : [])];
  return (
    <div data-testid="bar-page">
      <PageHeader title="Bar e gadget" subtitle="Segna cosa prende ogni camera e incassa al check-out" />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`bar-tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-sky-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
        {tab === "camere" && (
          <div className="flex items-center gap-2 ml-auto text-sm">
            <button onClick={() => shift(-1)} className="p-2 rounded-md border border-slate-200 bg-white" data-testid="bar-prev"><ChevronLeft size={16} /></button>
            <select value={m} onChange={(e) => setM(Number(e.target.value))} className="border border-slate-300 rounded-md px-2 py-2 bg-white" data-testid="bar-month">
              {MONTHS.map((n, i) => <option key={n} value={i}>{n}</option>)}
            </select>
            <input type="number" value={y} onChange={(e) => setY(Number(e.target.value) || y)} className="w-20 border border-slate-300 rounded-md px-2 py-2" data-testid="bar-year" />
            <button onClick={() => shift(1)} className="p-2 rounded-md border border-slate-200 bg-white" data-testid="bar-next"><ChevronRight size={16} /></button>
            <button onClick={() => { setY(now.getFullYear()); setM(now.getMonth()); }} data-testid="bar-today" className="px-3 py-2 rounded-md font-semibold text-sky-800 bg-sky-100">Oggi</button>
          </div>
        )}
      </div>
      {tab === "listino" ? <BarSettings onSaved={load} /> : (
        !data ? <div className="text-slate-400">Caricamento…</div> :
          data.rooms.length === 0 ? <div className="text-slate-500 bg-white border border-slate-200 rounded-xl p-6" data-testid="bar-empty">{"Nessuna camera occupata in questo mese."}</div> : (
            <>
              {data.rooms.some((r) => r.open_departed) && (
                <section className="mb-6 p-4 rounded-xl border border-rose-200 bg-rose-50/60" data-testid="bar-open-accounts">
                  <h2 className="font-semibold text-rose-800 mb-3">Conti aperti · clienti partiti con consumi da pagare</h2>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {data.rooms.filter((r) => r.open_departed).map((r) => <BarRoom key={r.booking_id} r={r} items={data.items} canEditPaid={isSuperAdmin} onChange={load} />)}
                  </div>
                </section>
              )}
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {data.rooms.filter((r) => !r.open_departed).map((r) => <BarRoom key={r.booking_id} r={r} items={data.items} canEditPaid={isSuperAdmin} onChange={load} />)}
              </div>
            </>
          )
      )}
    </div>
  );
}
